import Purchases from 'react-native-purchases'

import type {
  PurchaseCurrencyCode,
  WebBillingPackagesResult,
} from './webBillingOfferings.types'

const normalizeCurrencyCode = (currencyCode: string | null | undefined) =>
  currencyCode?.trim().toUpperCase() ?? ''

type WebOfferings = Awaited<ReturnType<typeof Purchases.getOfferings>>

export async function getWebBillingPackagesByCurrency(
  targetCurrency: PurchaseCurrencyCode,
  preferredOfferingIds: string[],
): Promise<WebBillingPackagesResult> {
  const normalizedTargetCurrency = normalizeCurrencyCode(targetCurrency)
  const offerings = (await Purchases.getOfferings()) as WebOfferings

  const offeringsById = offerings.all ?? {}
  const allOfferings = Object.values(offeringsById)
  const offeringPackageCounts = Object.fromEntries(
    Object.entries(offeringsById).map(([offeringId, offering]) => [
      offeringId,
      offering?.availablePackages.length ?? 0,
    ]),
  )
  const availableCurrencies = allOfferings
    .flatMap((offering) =>
      offering.availablePackages.map((pkg) =>
        normalizeCurrencyCode(pkg.product.currencyCode),
      ),
    )
    .filter(
      (currencyCode, index, allCurrencyCodes) =>
        currencyCode.length > 0 &&
        allCurrencyCodes.indexOf(currencyCode) === index,
    )

  const preferredOfferings = preferredOfferingIds
    .map((offeringId) => offeringsById[offeringId])
    .filter(
      (offering): offering is NonNullable<typeof offering> => offering != null,
    )

  const fallbackOfferings = [
    offerings.current,
    ...Object.values(offeringsById).filter(
      (offering) => offering != null && !preferredOfferings.includes(offering),
    ),
  ].filter(
    (offering): offering is NonNullable<typeof offering> => offering != null,
  )

  const orderedOfferings = [...preferredOfferings, ...fallbackOfferings]

  for (const offering of orderedOfferings) {
    const packagesByCurrency = offering.availablePackages.filter(
      (pkg) =>
        normalizeCurrencyCode(pkg.product.currencyCode) ===
        normalizedTargetCurrency,
    )
    if (packagesByCurrency.length > 0) {
      return {
        packages: packagesByCurrency,
        availableOfferingIds: Object.keys(offeringsById),
        currentOfferingIdentifier: offerings.current?.identifier ?? null,
        offeringPackageCounts,
        availableCurrencies,
        resolvedCurrency: normalizedTargetCurrency,
        usedCurrencyFallback: false,
      }
    }
  }

  for (const offering of orderedOfferings) {
    if (offering.availablePackages.length > 0) {
      const fallbackCurrency = normalizeCurrencyCode(
        offering.availablePackages[0]?.product.currencyCode,
      )

      return {
        packages: offering.availablePackages,
        availableOfferingIds: Object.keys(offeringsById),
        currentOfferingIdentifier: offerings.current?.identifier ?? null,
        offeringPackageCounts,
        availableCurrencies,
        resolvedCurrency: fallbackCurrency || null,
        usedCurrencyFallback: true,
      }
    }
  }

  return {
    packages: [],
    availableOfferingIds: Object.keys(offeringsById),
    currentOfferingIdentifier: offerings.current?.identifier ?? null,
    offeringPackageCounts,
    availableCurrencies,
    resolvedCurrency: null,
    usedCurrencyFallback: false,
  }
}
